import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LuChevronDown, LuChevronUp, LuChevronLeft, LuUser, LuX, LuKey, } from 'react-icons/lu';
import { MdDashboard } from "react-icons/md";
import { FaUserClock, FaCheckCircle, FaCalculator, FaUserCheck, FaVideo, FaCircle, FaHistory, FaLifeRing, FaQuestionCircle } from "react-icons/fa";
import { useAuth } from '../../providers/auth.provider';

interface DropdownItem {
    title: string;
    href: string;
}

interface SidebarItem {
    title: string;
    icon: React.ReactNode;
    href?: string;
    dropdownItems?: DropdownItem[];
}

interface SidebarProps {
    isCollapsed: boolean;
    setIsCollapsed: (value: boolean) => void;
    isMobileOpen: boolean;
    setIsMobileOpen: (value: boolean) => void;
}

const sidebarItems: SidebarItem[] = [
    { title: 'Home', icon: <MdDashboard className="w-5 h-5" />, href: '/' },
    { title: 'My Loan Profile', icon: <FaUserCheck className="w-5 h-5" />, href: '/loan' },
    { title: 'FAQs', icon: <FaQuestionCircle className="w-5 h-5" />, href: '/faqs' },
    { title: 'Help and Support', icon: <FaLifeRing className="w-5 h-5" />, href: '/help-and-support' },
    { title: 'Profile Details', icon: <LuKey className="w-5 h-5" />, href: '/profile' },
];

const Sidebar = ({ isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen }: SidebarProps) => {
    const [openDropdowns, setOpenDropdowns] = useState<{ [key: string]: boolean }>({});
    const [isHovered, setIsHovered] = useState(false);
    const location = useLocation();
    const { userData, partnerData } = useAuth();
    // Close mobile sidebar when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const sidebar = document.getElementById('sidebar');
            if (isMobileOpen && sidebar && !sidebar.contains(event.target as Node)) {
                setIsMobileOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isMobileOpen, setIsMobileOpen]);

    // Close mobile sidebar on navigation
    useEffect(() => {
        if (isMobileOpen) {
            setIsMobileOpen(false);
        }
    }, [location, setIsMobileOpen]);

    const toggleDropdown = (title: string) => {
        if (!isCollapsed || isHovered) {
            setOpenDropdowns(prev => ({
                ...prev,
                [title]: !prev[title]
            }));
        }
    };

    const isActive = (href: string) => {
        if (href === '/') {
            return location.pathname === '/';
        }
        return location.pathname.startsWith(href);
    };

    const effectiveCollapsed = isCollapsed && !isHovered;

    return (
        <>
            {/* Mobile overlay */}
            {isMobileOpen && (
                <div
                    className="fixed  inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 opacity-100 z-10 lg:hidden "
                    onClick={() => setIsMobileOpen(false)}
                />
            )}

            <aside
                id="sidebar"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className={`fixed top-0 left-0 h-full  ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}  ${effectiveCollapsed ? 'w-20' : 'w-72'}  lg:translate-x-0  transition-all duration-300 ease-in-out  bg-white border-r border-gray-200  flex flex-col  shadow-sm  z-10`}>
                <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
                    <div className="flex items-center justify-center w-full gap-3 overflow-hidden">
                        {/* <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary-900 flex items-center justify-center">
                            <LuBuilding2 className="w-6 h-6 text-white" />
                        </div>
                        {!effectiveCollapsed && (
                            <h1 className="text-xl font-bold text-gray-900 whitespace-nowrap">
                                LARK
                            </h1>
                        )} */}
                        <img src={partnerData?.company_logo ? partnerData?.company_logo : "/assets/images/logo.png"} alt="Logo" className='max-w-18 max-h-12' />
                    </div>
                    {isMobileOpen && (
                        <button
                            onClick={() => setIsMobileOpen(false)}
                            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
                        >
                            <LuX className="w-5 h-5 text-gray-600" />
                        </button>
                    )}
                    {!isMobileOpen && (isHovered || !isCollapsed) && (
                        <button
                            onClick={() => setIsCollapsed(!isCollapsed)}
                            className="hidden lg:flex p-1 hover:bg-gray-100 rounded-lg"
                        >
                            <LuChevronLeft className={`w-5 h-5 text-gray-600 ${isCollapsed ? 'rotate-180' : ''}`} />
                        </button>
                    )}
                </div>

                <nav className="flex-1 space-y-1 p-4 overflow-y-auto scrollbar-thin">
                    {sidebarItems.map((item) => (
                        <div key={item.title} className="relative">
                            {item.dropdownItems ? (
                                <>
                                    <button
                                        onClick={() => toggleDropdown(item.title)}
                                        className={`  w-full flex items-center justify-between p-3 rounded-lg  hover:bg-gray-100 hover:text-primary-900 transition-colors  ${openDropdowns[item.title] ? 'bg-gray-100' : ''}  ${effectiveCollapsed ? 'justify-center' : ''}`}>
                                        <div className={`flex items-center ${effectiveCollapsed ? 'justify-center' : 'space-x-3'}`}>
                                            <div className={`${isActive(item.dropdownItems[0].href) ? 'text-primary-900' : 'text-primary-900'}`}>
                                                {item.icon}
                                            </div>
                                            {!effectiveCollapsed && <span>{item.title}</span>}
                                        </div>
                                        {!effectiveCollapsed && (
                                            openDropdowns[item.title] ? (
                                                <LuChevronUp className="w-4 h-4" />
                                            ) : (
                                                <LuChevronDown className="w-4 h-4" />
                                            )
                                        )}
                                    </button>
                                    {openDropdowns[item.title] && !effectiveCollapsed && (
                                        <div className="pl-2 space-y-1 mt-1">
                                            {item.dropdownItems.map((dropdownItem) => (
                                                <Link
                                                    key={dropdownItem.href}
                                                    to={dropdownItem.href}
                                                    className={`flex gap-2 p-2 items-center rounded-lg transition-colors ${isActive(dropdownItem.href) ? 'bg-primary-900 text-white' : 'text-gray-600 hover:text-primary-900'}`}
                                                >
                                                    <FaCircle className='text-xs' />{dropdownItem.title}
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <Link
                                    to={item.href as string}
                                    className={`flex items-center ${effectiveCollapsed ? 'justify-center' : 'space-x-3'}  p-3 rounded-lg transition-colors  ${isActive(item.href!) ? 'bg-primary-900 text-white' : 'text-gray-600 hover:bg-gray-100  hover:text-primary-900'}`}              >
                                    <span className={`${!isActive(item.href as string) && 'text-primary-900'}`}>{item.icon}</span>
                                    {!effectiveCollapsed && <span>{item.title}</span>}
                                </Link>
                            )}
                        </div>
                    ))}
                </nav>

                <div className="p-4 border-t border-gray-200">
                    <div className={`flex items-center ${effectiveCollapsed ? 'justify-center' : 'space-x-3'}`}>
                        {!partnerData &&
                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                <LuUser className="w-5 h-5 text-gray-600" />
                            </div>
                        }
                        {!effectiveCollapsed && (
                            <div className="flex-1">
                                {partnerData ?
                                    <>
                                        <p className='flex items-center gap-2'>powered by <span className='font-semibold text-primary-800'><img src={"/assets/images/logo.png"} alt="Logo" className='max-w-20' /></span></p>
                                    </> : <>
                                        <p className="text-sm font-medium">{userData?.first_name}</p>
                                        <p className="text-xs text-gray-500">{userData?.email}</p>
                                    </>}
                            </div>
                        )}
                    </div>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;